/**
 * A document-context item as a mention node's attributes — the old web's
 * `documentContextMention.ts`. `selectedParams` is always `[]` here: the
 * parameter popover is the one part of the old chip not yet ported (item BI
 * notes it), so every marker the editor emits is the plain or family form.
 */
import type { DocumentContextItem } from "./documentContext";
import { requireDocumentContextElementId } from "./elementId";

/** The Revit index's drag payload type — the old web's, so the two never collide with text. */
export const REVIT_INDEX_MIME = "application/x-bimgo-revit-index-item";

/** Is a drag carrying an index row? `getData` is unreadable during dragover, so the type list is the only tell. */
export function isRevitIndexDrag(types: readonly string[]): boolean {
  return types.includes(REVIT_INDEX_MIME);
}

export interface DocumentContextMentionSource {
  type: string;
  id: string;
  name: string;
  familyName?: string;
  typeName?: string;
  derivedFromTypeId?: string;
  derivedFromTypeName?: string;
}

export interface DocumentContextMentionAttrs {
  id: string;
  label: string;
  type: string;
  familyName: string | null;
  typeName: string | null;
  derivedFromTypeId: string | null;
  derivedFromTypeName: string | null;
  selectedParams: [];
}

export function toDocumentContextMentionSource(type: string, item: DocumentContextItem): DocumentContextMentionSource {
  return {
    type,
    id: requireDocumentContextElementId(type, item.id),
    name: item.name,
    ...(typeof item.familyName === "string" ? { familyName: item.familyName } : {}),
    ...(typeof item.typeName === "string" ? { typeName: item.typeName } : {}),
    ...(typeof item.derivedFromTypeId === "string" ? { derivedFromTypeId: item.derivedFromTypeId } : {}),
    ...(typeof item.derivedFromTypeName === "string" ? { derivedFromTypeName: item.derivedFromTypeName } : {}),
  };
}

export function toDocumentContextMentionAttrs(source: DocumentContextMentionSource): DocumentContextMentionAttrs {
  return {
    id: source.id,
    label: source.name,
    type: source.type,
    familyName: source.familyName ?? null,
    typeName: source.typeName ?? null,
    derivedFromTypeId: source.derivedFromTypeId ?? null,
    derivedFromTypeName: source.derivedFromTypeName ?? null,
    selectedParams: [],
  };
}

/**
 * The drag payload back into a source — strictly: an object, the three
 * identity fields non-empty strings, every optional field a string or absent.
 * Anything else is null, and the drop is ignored rather than inserting a
 * half-formed chip.
 */
export function parseDocumentContextMentionSource(raw: string): DocumentContextMentionSource | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  const required = ["type", "id", "name"] as const;
  for (const key of required) {
    if (typeof item[key] !== "string" || (item[key] as string).length === 0) return null;
  }
  try {
    requireDocumentContextElementId(item["type"] as string, item["id"]);
  } catch {
    return null;
  }
  const optional = ["familyName", "typeName", "derivedFromTypeId", "derivedFromTypeName"] as const;
  for (const key of optional) {
    if (item[key] !== undefined && typeof item[key] !== "string") return null;
  }
  const source: DocumentContextMentionSource = { type: item["type"] as string, id: item["id"] as string, name: item["name"] as string };
  for (const key of optional) {
    if (typeof item[key] === "string") source[key] = item[key] as string;
  }
  return source;
}
